import { useMemo, useState } from 'react';
import { BarChartDataItem, ChartColors, MappingList, MappingListItem } from '@silverfort/sf-components';
import { QueryFilters } from '../api';
import { useAuthenticationFirewallMetrics } from './useAuthenticationFirewallMetrics';
import { getDateShortFormat } from '../../../utils/dates';
import { AllAuthenticationsTraffic } from '../types';

export type AuthenticationsTrafficTab = 'all' | 'blocked';

interface UseAuthenticationsTrafficBarChartProps {
    filters: QueryFilters;
}

interface UseAuthenticationsTrafficBarChartReturn {
    selectedTab: AuthenticationsTrafficTab;
    setSelectedTab: (tab: AuthenticationsTrafficTab) => void;
    barChartData: BarChartDataItem[];
    isLoading: boolean;
    barChartVariant: 'single' | 'stacked';
    mappingList: MappingListItem[];
}

export const useAuthenticationsTrafficBarChart = ({
    filters
}: UseAuthenticationsTrafficBarChartProps): UseAuthenticationsTrafficBarChartReturn => {
    const [selectedTab, setSelectedTab] = useState<AuthenticationsTrafficTab>('blocked');

    const { allAuthenticationsTraffic, allAuthenticationsTrafficIsLoading } = useAuthenticationFirewallMetrics(filters);

    const barChartVariant = selectedTab === 'all' ? 'stacked' : 'single';
    const isLoading = allAuthenticationsTrafficIsLoading;
    const rawData = allAuthenticationsTraffic;

    const barChartData = useMemo(() => {
        if (!rawData) return [];

        return rawData.map((item) => {
            if (selectedTab === 'all') {
                const allAuthenticationsTraffic = item as AllAuthenticationsTraffic;
                const hideTooltip = allAuthenticationsTraffic.total === 0 && allAuthenticationsTraffic.blocked === 0;
                return {
                    date: allAuthenticationsTraffic.date,
                    variant: 'stacked' as const,
                    value: allAuthenticationsTraffic.blocked,
                    stackedValue: allAuthenticationsTraffic.total - allAuthenticationsTraffic.blocked,
                    tooltipProps: hideTooltip ? undefined : {
                        title: getDateShortFormat(allAuthenticationsTraffic.date),
                        titleClassName: 'font-bold text-[10px] underline',
                        description: (
                            <MappingList
                                items={[
                                    {
                                        name: 'All',
                                        value: allAuthenticationsTraffic.total,
                                        fill: ChartColors['data-bg-weak-2'],
                                        className: 'text-sf-white text-[10px] gap-1'
                                    },
                                    {
                                        name: 'Blocked',
                                        value: allAuthenticationsTraffic.blocked,
                                        fill: ChartColors['data-bg-main-2'],
                                        className: 'text-sf-white text-[10px] gap-1'
                                    }
                                ]}
                                isLoading={false}
                                containerClassName="[&]:gap-1"
                            />
                        )
                    }
                };
            }

            return {
                date: item.date,
                variant: 'single' as const,
                value: item.blocked,
                tooltipProps: item.blocked === 0 ? undefined : {
                    title: getDateShortFormat(item.date),
                    titleClassName: 'font-bold text-[10px] underline',
                    description: (
                        <MappingList
                            items={[
                                {
                                    name: 'Blocked',
                                    value: item.blocked,
                                    fill: ChartColors['data-bg-main-2'],
                                    className: 'text-sf-white text-[10px] gap-1'
                                }
                            ]}
                            isLoading={false}
                        />
                    )
                }
            };
        });
    }, [rawData, selectedTab]);

    const mappingList = useMemo(() => {
        if (!rawData) return [];

        if (selectedTab === 'all') {
            return [
                {
                    name: 'All authentications',
                    fill: ChartColors['data-bg-weak-2'],
                    value: 0
                },
                {
                    name: 'Blocked',
                    fill: ChartColors['data-bg-main-2'],
                    value: 0
                }
            ];
        }
        return [
            {
                name: 'Blocked',
                fill: ChartColors['data-bg-main-2'],
                value: 0
            }
        ];
    }, [rawData, selectedTab]);

    return {
        selectedTab,
        setSelectedTab,
        barChartData,
        isLoading,
        barChartVariant,
        mappingList
    };
};
